import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Spinner from "../../../components/Spinner";
import AuthContext from "../../../context/auth/AuthContext";
import LandLordContext from "../../../context/landlord/LandLordContext";
import "react-toastify/dist/ReactToastify.css";
import useTranslator from "../../../hooks/useTranslator";
import { haveValue } from "../../../helper/common";
import searchicon from "../../../assets/img/s.png";
import PropertyTable from "./PropertyTable";
import Fuse from "fuse.js";

const Property = () => {
  const { _t } = useTranslator();

  const navigate = useNavigate();

  const authContext = useContext(AuthContext);
  const { user } = authContext;

  const landLordContext = useContext(LandLordContext);
  const {
    landlord_loading,
    properties,
    loadProperties,
    getAgencyAgents,
    getTanentData,
  } = landLordContext;

  const [searchVal, setSearchVal] = useState("");
  const [filteredProperty, setFilteredProperty] = useState(properties);
  const [searchedProp, setSearchedProp] = useState(properties);
  const [filterVal, setFilterVals] = useState({
    stairway: [],
    floorNumber: [],
    letter: [],
    agent: [],
  });
  const [filterCheck, setFilterCheck] = useState({
    stairway: "",
    floorNumber: "",
    letter: "",
    property: "available",
    agent: "",
  });
  const [tanentData, setTanentData] = useState([]);

  const loadData = () => {
    loadProperties(user?._id).then((res) => {
      if (haveValue(properties)) {
        const stairway = new Set();
        const floorNumber = new Set();
        const letter = new Set();
        properties.map((val) => {
          stairway.add(val?.staircase);
          floorNumber.add(val?.floor_number);
          letter.add(val?.flat_number);
        });
        setFilterVals((prev) => ({
          stairway: [...stairway],
          floorNumber: [...floorNumber],
          letter: [...letter],
          agent: prev.agent,
        }));
      }
    });
    getAgencyAgents()
      .then((res) => {
        const agentList = res?.data?.data;
        if (haveValue(agentList) && Array.isArray(agentList)) {
          setFilterVals((prev) => ({
            ...prev,
            agent: agentList,
          }));
        }
      })
      .catch((err) => {
        console.log(err, "Error inside getAgencyAgents");
      });
  };

  const fetchTanentData = async () => {
    try {
      const tanentData = await getTanentData();
      const dataArray = Array.isArray(tanentData?.data) ? tanentData.data : [];

      const updatedData = dataArray.map((item) => ({
        label: item?.tenant_id?.fname + " " + (item?.tenant_id?.lname ?? ""),
        value: item?.tenant_id?._id,
      }));

      setTanentData(updatedData);
    } catch (error) {
      console.error("Error fetching tenant data:", error);
    }
  };

  useEffect(() => {
    fetchTanentData();
    loadData();
  }, []);

  useEffect(() => {
    setFilterCheck((prev) => ({
      ...prev,
      property: user?.isParentLL ? "all" : "available",
    }));
  }, [user]);

  useEffect(() => {
    setFilteredProperty(properties);
  }, [properties]);

  const addSearchVal = (e) => {
    const enteredVal = e.target.value;
    setSearchVal(enteredVal);
  };

  useEffect(() => {
    searchProperty();
  }, [searchVal, properties]);


  const normalize = (str) =>
    str
      ?.toString()
      .toLowerCase()
      .replace(/[^\w\s]/gi, "")
      .trim() || "";

  const searchProperty = () => {
    if (!haveValue(properties)) return;

    if (haveValue(searchVal)) {
      const normalizedSearchVal = normalize(searchVal);
      const tokens = normalizedSearchVal.split(" ").filter(Boolean);

      const result = properties.filter((prop) => {
        const searchString = normalize(
          [
            prop?.street,
            prop?.street_number,
            prop?.staircase,
            prop?.floor_number,
            prop?.flat_number,
            prop?.postcode,
            prop?.property_reference,
          ]
            .filter(Boolean)
            .join(" ")
        );

        return tokens.every((token) => searchString.includes(token));
      });

      // Extract filter values
      const stairway = new Set();
      const floorNumber = new Set();
      const letter = new Set();
      result.forEach((val) => {
        stairway.add(val?.staircase);
        floorNumber.add(val?.floor_number);
        letter.add(val?.flat_number);
      });

      setFilterVals((prev) => ({
        stairway: [...stairway],
        floorNumber: [...floorNumber],
        letter: [...letter],
        agent: prev.agent,
      }));

      setFilterCheck({ stairway: "", floorNumber: "", letter: "" });
      setFilteredProperty(result);
      setSearchedProp(result);
    } else {
      // Reset filters
      const stairway = new Set();
      const floorNumber = new Set();
      const letter = new Set();
      properties.forEach((val) => {
        stairway.add(val?.staircase);
        floorNumber.add(val?.floor_number);
        letter.add(val?.flat_number);
      });

      setFilterVals((prev) => ({
        stairway: [...stairway],
        floorNumber: [...floorNumber],
        letter: [...letter],
        agent: prev.agent,
      }));

      setFilterCheck({
        stairway: "",
        floorNumber: "",
        letter: "",
        property: user?.isParentLL ? "all" : "available",
      });

      setFilteredProperty(properties);
      setSearchedProp(properties);
    }
  };

  const handleFilterSelected = (type, e) => {
    setFilteredProperty(searchedProp);
    if (type == "stairway") {
      setFilterCheck((prev) => ({
        stairway: e.target.value,
        letter: "",
        floorNumber: "",
      }));
    }
    if (type == "floorNumber") {
      setFilterCheck((prev) => ({
        ...prev,
        letter: "",
        floorNumber: e.target.value,
      }));
    }
    if (type == "letter") {
      setFilterCheck((prev) => ({ ...prev, letter: e.target.value }));
    }
    if (type == "agent") {
      setFilterCheck((prev) => ({ ...prev, agent: e.target.value }));
    }

    if (type == "property") {
      const value = e.target.value;
      setFilterCheck((prev) => ({ ...prev, property: value }));
    }
    setFilterCheck((prev) => ({ ...prev, [type]: e.target.value }));
  };

  useEffect(() => {
    filterSearchedProp();
  }, [filterCheck]);

  const filterSearchedProp = () => {
    if (haveValue(filteredProperty)) {
      var temp = filteredProperty;
      if (haveValue(filterCheck.stairway)) {
        temp = temp.filter((val) => val?.staircase == filterCheck.stairway);

        const floorNumber = new Set();
        const letter = new Set();
        temp.map((val) => {
          floorNumber.add(val?.floor_number);
          letter.add(val?.flat_number);
        });

        setFilterVals((prev) => ({
          ...prev,
          floorNumber: [...floorNumber],
          letter: [...letter],
        }));
      }
      if (haveValue(filterCheck.floorNumber)) {
        temp = temp.filter(
          (val) => val?.floor_number == filterCheck.floorNumber
        );
        const letter = new Set();
        temp.map((val) => {
          letter.add(val?.flat_number);
        });

        setFilterVals((prev) => ({ ...prev, letter: [...letter] }));
      }
      if (haveValue(filterCheck.letter)) {
        temp = temp.filter((val) => val?.flat_number == filterCheck.letter);
      }
      if (haveValue(filterCheck.agent)) {
        temp = temp.filter((val) => val?.landlord?._id == filterCheck.agent);
      }
      if (haveValue(filterCheck.property)) {
        if (filterCheck.property === "awarded") {
          temp = temp.filter((val) => val?.is_awarded === true);
        }

        if (filterCheck.property === "available") {
          temp = temp.filter((val) => {
            if (
              !val.hasOwnProperty("is_awarded") ||
              (val.hasOwnProperty("is_awarded") && val?.is_awarded === false)
            ) {
              return true;
            }

            return false;
          });
        }
      }
      if (
        !haveValue(filterCheck.stairway) &&
        !haveValue(filterCheck.floorNumber) &&
        !haveValue(filterCheck.letter)
      ) {
        const stairway = new Set();
        const floorNumber = new Set();
        const letter = new Set();
        filteredProperty.map((val) => {
          stairway.add(val?.staircase);
          floorNumber.add(val?.floor_number);
          letter.add(val?.flat_number);
        });

        setFilterVals((prev) => ({
          stairway: [...stairway],
          floorNumber: [...floorNumber],
          letter: [...letter],
          agent: prev.agent,
        }));
      }

      setFilteredProperty(temp);
    }
  };

  const markPropertyAwarded = (propertyId) => {
    if (haveValue(propertyId) && haveValue(filteredProperty)) {
      loadData();
    }
  };

  if (landlord_loading) return <Spinner />;

  return (
    <>
      {haveValue(properties) && properties.length > 0 ? (
        <>
          <div className="tabs-box-pattern mobile big_tablebg">
            <div className="tabs-item d-block position-relative">
              <div
                className="number-properties"
                style={{
                  top: `${
                    document.getElementById("header")
                      ? document.getElementById("header").offsetHeight
                      : 10
                  }px`,
                }}
              >
                <div className="search-property">
                <div className="search_bar_edit">
                  <input
                    type="text"
                    className="search-prop-landlord custom-input mb-0 input01 wsetplaceholder"
                    onChange={addSearchVal}
                    value={searchVal}
                    placeholder={_t("searchProperty")}
                  />
                  <a href="#">
                    <img src={searchicon} alt="" />
                  </a>
                </div>
                </div>

                {user?.isParentLL ? (
                  <>
                    <div className="filterDiv">
                      <select
                        name="agentFilter"
                        id="agentFilter"
                        onChange={(e) => handleFilterSelected("agent", e)}
                        value={filterCheck.agent}
                      >
                        <option value="">{_t(
                          "agentFilter",
                          "Used in parent landlord dashboard for filtering "
                        )}</option>
                        {filterVal.agent.map((val) =>
                          haveValue(val) ? (
                            <option value={val?._id}>
                              {val?.fname_companyName} {val?.lname}
                            </option>
                          ) : (
                            <></>
                          )
                        )}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                  </>
                )}
                <div className="filterDiv">
                  <select
                    name="propertyFilter"
                    id="propertyFilter"
                    onChange={(e) => handleFilterSelected("property", e)}
                    value={filterCheck.property}
                  >
                    <option value="available">{_t("available")}</option>
                    <option value="all">{_t("filterProperty","On landlord side for filtering purpose")}</option>
                    <option value="awarded">{_t("awarded")}</option>
                  </select>
                </div>

                <Link to="/landlord/add-property">
                  <button className="btn btngreen cstbtn">
                    + {_t("addProperty")}
                  </button>
                </Link>
              </div>

              <div className="row property_margin">
                <PropertyTable
                  property={filteredProperty}
                  onPropertyAward={markPropertyAwarded}
                  tanentData={tanentData}
                  loadData={loadData}
                />

                {(!filteredProperty || filteredProperty.length === 0) && (
                  <div className="tabs-box-pattern big_tablebg">
                    <div className="tabs-item d-block">
                      <div className="landlord_homepage ">
                        <div className="addpropertybar">
                          <img src="/images/house.svg" alt="" />
                          <h4>{_t("noPropertiesYet")}</h4>
                          <p>{_t("pleaseAddYourProperties")}</p>
                          <div>
                            <button
                              className="btn btngreen"
                              onClick={(e) =>
                                navigate("/landlord/add-property")
                              }
                            >
                              + {_t("addProperty")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="tabs-box-pattern">
            <div className="tabs-item d-block">
              <div className="landlord_homepage ">
                <div className="addpropertybar">
                  <img src="/images/house.svg" alt="" />
                  <h4>{_t("noPropertiesYet")}</h4>
                  <p>{_t("pleaseAddYourProperties")}</p>
                  <div>
                    <button
                      className="btn btngreen"
                      onClick={(e) => navigate("/landlord/add-property")}
                    >
                      + {_t("addProperty")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Property;
